import React, { useEffect, useRef } from 'react';

const WaterRippleBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width = 0;
    let height = 0;

    // Simulation grid dimensions (low res for extreme speed)
    const simWidth = 128;
    const simHeight = 72;
    const size = simWidth * simHeight;

    let buffer1 = new Float32Array(size);
    let buffer2 = new Float32Array(size);

    const damping = 0.97;
    const refractionStrength = 22; // strength of water distortion
    const specularFactor = 85; // strength of glossy light highlights

    // Refraction render dimensions (smooth stretch)
    const renderWidth = 320;
    const renderHeight = 180;

    const resize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // Track mouse
    let mx = -100;
    let my = -100;
    let lastMx = -100;
    let lastMy = -100;

    const onMouseMove = (e) => {
      mx = e.clientX;
      my = e.clientY;
    };

    const onClick = (e) => {
      // Create big splash on click
      addDrop(e.clientX, e.clientY, 4, 30);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('click', onClick);

    // Touch support
    const onTouchMove = (e) => {
      if (e.touches.length > 0) {
        mx = e.touches[0].clientX;
        my = e.touches[0].clientY;
      }
    };
    window.addEventListener('touchmove', onTouchMove, { passive: true });

    // Add ripple drop to buffer1
    const addDrop = (x, y, radius, strength) => {
      const sx = Math.floor((x / window.innerWidth) * simWidth);
      const sy = Math.floor((y / window.innerHeight) * simHeight);

      if (sx < radius || sx >= simWidth - radius || sy < radius || sy >= simHeight - radius) return;

      for (let j = -radius; j <= radius; j++) {
        for (let i = -radius; i <= radius; i++) {
          const nx = sx + i;
          const ny = sy + j;
          const dist = Math.sqrt(i * i + j * j);
          if (dist < radius) {
            const weight = (1.0 - dist / radius) * strength;
            buffer1[ny * simWidth + nx] += weight;
          }
        }
      }
    };

    // Offscreen background canvas to draw glowing glass blobs
    const bgCanvas = document.createElement('canvas');
    bgCanvas.width = renderWidth;
    bgCanvas.height = renderHeight;
    const bgCtx = bgCanvas.getContext('2d');

    // Offscreen render canvas to hold the refracted image data
    const renderCanvas = document.createElement('canvas');
    renderCanvas.width = renderWidth;
    renderCanvas.height = renderHeight;
    const renderCtx = renderCanvas.getContext('2d');

    let time = 0;
    let frameId = 0;

    const render = () => {
      time += 0.003;

      // 1. Draw glowing color gradients / blobs on background canvas
      // Background base
      bgCtx.fillStyle = '#0a0a0c';
      bgCtx.fillRect(0, 0, renderWidth, renderHeight);

      // Draw flowing glass background orbs
      // Orb 1 (Deep Indigo / Violet)
      const ox1 = renderWidth / 2 + Math.sin(time) * (renderWidth * 0.35);
      const oy1 = renderHeight / 2 + Math.cos(time * 0.8) * (renderHeight * 0.35);
      const g1 = bgCtx.createRadialGradient(ox1, oy1, 5, ox1, oy1, 120);
      g1.addColorStop(0, 'rgba(49, 46, 129, 0.45)'); // indigo
      g1.addColorStop(0.5, 'rgba(67, 56, 202, 0.15)');
      g1.addColorStop(1, 'transparent');
      bgCtx.fillStyle = g1;
      bgCtx.beginPath();
      bgCtx.arc(ox1, oy1, 120, 0, Math.PI * 2);
      bgCtx.fill();

      // Orb 2 (Teal / Cyan)
      const ox2 = renderWidth / 2 + Math.sin(time * 0.7 + 2.5) * (renderWidth * 0.35);
      const oy2 = renderHeight / 2 + Math.cos(time * 1.1 + 1.2) * (renderHeight * 0.35);
      const g2 = bgCtx.createRadialGradient(ox2, oy2, 5, ox2, oy2, 90);
      g2.addColorStop(0, 'rgba(13, 148, 136, 0.35)'); // teal
      g2.addColorStop(0.5, 'rgba(15, 118, 110, 0.12)');
      g2.addColorStop(1, 'transparent');
      bgCtx.fillStyle = g2;
      bgCtx.beginPath();
      bgCtx.arc(ox2, oy2, 90, 0, Math.PI * 2);
      bgCtx.fill();

      // Orb 3 (Rich Blue / Slate)
      const ox3 = renderWidth / 2 + Math.sin(time * 0.5 - 1.5) * (renderWidth * 0.4);
      const oy3 = renderHeight / 2 + Math.cos(time * 0.9 - 2) * (renderHeight * 0.4);
      const g3 = bgCtx.createRadialGradient(ox3, oy3, 5, ox3, oy3, 100);
      g3.addColorStop(0, 'rgba(29, 78, 216, 0.35)'); // rich blue
      g3.addColorStop(1, 'transparent');
      bgCtx.fillStyle = g3;
      bgCtx.beginPath();
      bgCtx.arc(ox3, oy3, 100, 0, Math.PI * 2);
      bgCtx.fill();

      // Trigger soft mouse ripple drops
      if (mx !== lastMx || my !== lastMy) {
        if (lastMx !== -100) {
          addDrop(mx, my, 2.2, 5);
        }
        lastMx = mx;
        lastMy = my;
      }

      // 2. Wave propagation physics step
      for (let y = 1; y < simHeight - 1; y++) {
        const row = y * simWidth;
        const prevRow = (y - 1) * simWidth;
        const nextRow = (y + 1) * simWidth;
        for (let x = 1; x < simWidth - 1; x++) {
          const idx = row + x;
          buffer2[idx] = (
            (buffer1[idx - 1] +
              buffer1[idx + 1] +
              buffer1[prevRow + x] +
              buffer1[nextRow + x]) / 2.0
          ) - buffer2[idx];
          buffer2[idx] *= damping;
        }
      }

      // Swap heightmaps
      const temp = buffer1;
      buffer1 = buffer2;
      buffer2 = temp;

      // 3. Render displacement / refraction mapping
      const bgImg = bgCtx.getImageData(0, 0, renderWidth, renderHeight);
      const bgData = bgImg.data;

      const renderImg = renderCtx.createImageData(renderWidth, renderHeight);
      const renderData = renderImg.data;

      const simScaleX = simWidth / renderWidth;
      const simScaleY = simHeight / renderHeight;

      for (let y = 0; y < renderHeight; y++) {
        const rowOffset = y * renderWidth;
        const sy = Math.floor(y * simScaleY);
        const simRow = sy * simWidth;
        const prevSimRow = Math.max(0, sy - 1) * simWidth;
        const nextSimRow = Math.min(simHeight - 1, sy + 1) * simWidth;

        for (let x = 0; x < renderWidth; x++) {
          const sx = Math.floor(x * simScaleX);
          
          let dx = 0;
          let dy = 0;

          if (sx > 0 && sx < simWidth - 1 && sy > 0 && sy < simHeight - 1) {
            const idx = simRow + sx;
            dx = buffer1[idx + 1] - buffer1[idx - 1];
            dy = buffer1[nextSimRow + sx] - buffer1[prevSimRow + sx];
          }

          // Calculate offset pixel lookup positions
          const lookupX = Math.min(renderWidth - 1, Math.max(0, x + Math.floor(dx * refractionStrength)));
          const lookupY = Math.min(renderHeight - 1, Math.max(0, y + Math.floor(dy * refractionStrength)));

          const bgIdx = (lookupY * renderWidth + lookupX) * 4;
          const pixelIdx = (rowOffset + x) * 4;

          // Copy refracted RGB
          const r = bgData[bgIdx];
          const g = bgData[bgIdx + 1];
          const b = bgData[bgIdx + 2];

          // Compute glossy highlight based on normal slopes
          const highlight = Math.max(0, dx + dy) * specularFactor;

          renderData[pixelIdx] = Math.min(255, r + highlight);
          renderData[pixelIdx + 1] = Math.min(255, g + highlight);
          renderData[pixelIdx + 2] = Math.min(255, b + highlight * 1.15); // Cooler blue specular highlight
          renderData[pixelIdx + 3] = 255;
        }
      }

      // Draw refracted image on intermediate offscreen render canvas
      renderCtx.putImageData(renderImg, 0, 0);

      // Clear main on-screen canvas
      ctx.fillStyle = '#0a0a0c';
      ctx.fillRect(0, 0, width, height);

      // Draw stretched refracted canvas to cover screen
      ctx.drawImage(renderCanvas, 0, 0, width, height);

      // Draw subtle noise texture/overlay
      ctx.fillStyle = 'rgba(255, 255, 255, 0.015)';
      ctx.globalCompositeOperation = 'difference';
      ctx.fillRect(0, 0, width, height);
      ctx.globalCompositeOperation = 'source-over';

      frameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('click', onClick);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 0,
        pointerEvents: 'none',
        background: '#0a0a0c',
      }}
    />
  );
};

export default WaterRippleBackground;
